// Vendor Billing v1.0 — VFI invoice drawer (record detail per the Entity
// Detail Page Layout Standard: drawers for detail, modals for edits — the
// three lightweight actions render as inline confirm sections inside the
// drawer). Actions: Void (reason required), Record manual payment (amount
// fixed to the invoice amount — v1 is full-payment-only), and the
// ADMIN-only manual status correction (the refunds / mistakes escape
// hatch).

import { useState, type ReactNode } from "react";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import {
  calendarDateInputToIso,
  formatCalendarDate,
  isoDateInTz,
  useDateFormatter,
  useUserTimeZone,
} from "@/utils/datetime";
import {
  useCorrectInvoiceStatus,
  useRecordManualPayment,
  useRefreshInvoicePayment,
  useVendorInvoice,
  useVoidVendorInvoice,
} from "@/hooks/portfolioAdmin/useVendorBilling";
import type { VendorInvoiceDto, VendorInvoiceStatus } from "@/api/portfolioAdmin/types";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";
import {
  fmtMoney,
  INVOICE_STATUS_LABEL,
  MANUAL_PAYABLE_STATUSES,
  VOIDABLE_STATUSES,
} from "./vendorBillingFormat";

type ActionMode = null | "void" | "payment" | "correct";

/**
 * Step 6 W3 — the band an invoice was priced in, from the values copied at
 * issue. Deliberately reads from the snapshot columns and never resolves the
 * live band: the point of copying was that the live row may be gone.
 */
function describeBandAtIssue(invoice: VendorInvoiceDto): string {
  const lower = invoice.contractTierLowerThresholdAtIssue ?? 0;
  const upper = invoice.contractTierUpperThresholdAtIssue;
  const rate = invoice.contractTierRateAtIssue ?? 0;
  const range = upper == null ? `above ${fmtMoney(lower)}` : `${fmtMoney(lower)}–${fmtMoney(upper)}`;
  const priced =
    invoice.contractTierRateTypeAtIssue === "Flat"
      ? `${fmtMoney(rate)} flat`
      : `${(rate * 100).toLocaleString("en-US", { maximumFractionDigits: 4 })}%`;
  return `${range} at ${priced}`;
}

interface VendorInvoiceDrawerProps {
  invoiceId: string;
  open: boolean;
  onClose: () => void;
}

export function VendorInvoiceDrawer({
  invoiceId,
  open,
  onClose,
}: VendorInvoiceDrawerProps) {
  const toast = useToast();
  const auth = useAuth();
  // 🔴 `dueDate` is the ONLY wall-calendar field on this drawer. This comment
  // used to read "due, paid, received, voided", and that sentence is what put
  // four clock stamps on the UTC renderer: `IssuedAt`, `VoidedAt` and
  // `ReversedAt` have no writer but `DateTimeOffset.UtcNow`, so anything done
  // after 5pm Phoenix rendered as TOMORROW.
  //
  // `paidAt` / `receivedAt` stay on `formatCalendarDate` deliberately — they are
  // MIXED at the data layer and neither renderer is right for every row. See the
  // note at the two `paidAt` / `receivedAt` render sites below.
  const { date } = useDateFormatter();
  const tz = useUserTimeZone();
  const detailQuery = useVendorInvoice(invoiceId);

  const voidMutation = useVoidVendorInvoice(invoiceId);
  const paymentMutation = useRecordManualPayment(invoiceId);
  const correctMutation = useCorrectInvoiceStatus(invoiceId);
  const refreshMutation = useRefreshInvoicePayment(invoiceId);

  const [mode, setMode] = useState<ActionMode>(null);
  const [reason, setReason] = useState("");
  // Today in the USER's timezone — `.toISOString().slice(0,10)` gave a US
  // user tomorrow's date after 5pm.
  const [paymentDate, setPaymentDate] = useState(() => isoDateInTz(new Date(), tz));
  const [paymentNote, setPaymentNote] = useState("");
  const [correctTo, setCorrectTo] = useState<VendorInvoiceStatus>("issued");

  const detail = detailQuery.data;
  const invoice = detail?.invoice;

  const canVoid = auth.hasPermission("VendorBilling.Void");
  const canRecordPayment = auth.hasPermission("VendorBilling.RecordManualPayment");
  const canCorrect = auth.hasRoleCode("ADMIN");

  const fail = (err: unknown, fallback: string) =>
    toast.error(
      err instanceof ApiError && err.errors.length
        ? err.errors.join("; ")
        : err instanceof Error
          ? err.message
          : fallback,
    );

  const resetForms = () => {
    setMode(null);
    setReason("");
    setPaymentNote("");
  };

  const onVoid = async () => {
    try {
      await voidMutation.mutateAsync({ reason: reason.trim() });
      toast.success("Invoice voided.");
      resetForms();
    } catch (err) {
      fail(err, "Could not void the invoice.");
    }
  };

  const onRecordPayment = async () => {
    try {
      await paymentMutation.mutateAsync({
        // A receipt date is a wall-calendar date, not an instant: noon UTC
        // survives being re-rendered in any timezone as the day that was typed.
        receivedAt: calendarDateInputToIso(paymentDate),
        note: paymentNote.trim() || null,
      });
      toast.success("Manual payment recorded — invoice is paid.");
      resetForms();
    } catch (err) {
      fail(err, "Could not record the payment.");
    }
  };

  const onCorrect = async () => {
    try {
      await correctMutation.mutateAsync({ toStatus: correctTo, reason: reason.trim() });
      toast.success(`Status corrected to ${INVOICE_STATUS_LABEL[correctTo]}.`);
      resetForms();
    } catch (err) {
      fail(err, "Could not correct the status.");
    }
  };

  const onRefreshPayment = async () => {
    try {
      const detail = await refreshMutation.mutateAsync();
      toast.success(
        `Payment status refreshed — invoice is ${INVOICE_STATUS_LABEL[detail.invoice.status]}.`,
      );
    } catch (err) {
      fail(err, "Could not refresh the payment status.");
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={invoice ? invoice.invoiceNumber : "Invoice"}
      subtitle={invoice ? `${invoice.vendorName} · ${invoice.loanNumber}` : undefined}
      testId="vendor-invoice-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Close</Button>
          {invoice && canRecordPayment
            && MANUAL_PAYABLE_STATUSES.includes(invoice.status) && (
            <Button
              variant="primary"
              onClick={() => setMode(mode === "payment" ? null : "payment")}
              data-testid="invoice-record-payment-button"
            >
              Record manual payment
            </Button>
          )}
          {invoice && canVoid && VOIDABLE_STATUSES.includes(invoice.status) && (
            <Button
              variant="destructive"
              onClick={() => setMode(mode === "void" ? null : "void")}
              data-testid="invoice-void-button"
            >
              Void
            </Button>
          )}
        </>
      }
    >
      {detailQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading invoice…</p>
      )}
      {detailQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load the invoice. Refresh to retry.
        </p>
      )}
      {invoice && (
        <div className="flex flex-col gap-space-4">
          <section>
            <div className="flex items-center justify-between gap-space-2 mb-space-2">
              <h3 className="font-display font-semibold text-h4 m-0">Invoice</h3>
              <InvoiceStatusBadge status={invoice.status} />
            </div>
            <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
              <dt className="text-caption text-gray-mid uppercase tracking-label">Amount</dt>
              <dd className="m-0 text-right tabular-nums">{fmtMoney(invoice.amount)}</dd>
              {invoice.audience === "borrower" && (
                <>
                  <dt className="text-caption text-gray-mid uppercase tracking-label">Bills</dt>
                  <dd className="m-0" data-testid="invoice-audience">
                    The borrower (regulated vendor — billed through Phoenix)
                  </dd>
                </>
              )}
              <dt className="text-caption text-gray-mid uppercase tracking-label">Loan</dt>
              <dd className="m-0">{invoice.loanNumber}</dd>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Issued</dt>
              <dd className="m-0">{invoice.issuedAt ? date(invoice.issuedAt) : "—"}</dd>
              <dt className="text-caption text-gray-mid uppercase tracking-label">Due</dt>
              <dd className="m-0">{invoice.dueDate ? formatCalendarDate(invoice.dueDate) : "—"}</dd>
              <dt className="text-caption text-gray-mid uppercase tracking-label">First viewed</dt>
              <dd className="m-0">{invoice.viewedAt ? date(invoice.viewedAt) : "Not yet viewed"}</dd>
              {invoice.paidAt && (
                <>
                  <dt className="text-caption text-gray-mid uppercase tracking-label">Paid</dt>
                  <dd className="m-0">
                    {/* 🔴 MIXED AT THE DATA LAYER — reported, not changed.
                        `VendorInvoice.PaidAt` has three writers: Stripe
                        settlement (`StripePaymentService`, the clock), a status
                        correction (`VendorInvoiceService`, the clock), and a
                        manually recorded payment, which stores the day an
                        operator picked. Since #1032 that pick is written at NOON
                        UTC, which renders identically either way — but rows
                        recorded between #617 and #1032 are at MIDNIGHT, and
                        those would move a day early under a tz-aware renderer.
                        So neither renderer is right for every row, and the
                        populations cannot be sized from the client. The fix is
                        server-side: a companion `DateOnly`, or a backfill of the
                        pre-#1032 rows to noon. */}
                    {formatCalendarDate(invoice.paidAt)}
                    {invoice.paymentMethod ? ` · ${invoice.paymentMethod}` : ""}
                  </dd>
                </>
              )}
              {invoice.voidedAt && (
                <>
                  <dt className="text-caption text-gray-mid uppercase tracking-label">Voided</dt>
                  <dd className="m-0">{date(invoice.voidedAt)}</dd>
                  <dt className="text-caption text-gray-mid uppercase tracking-label">Void reason</dt>
                  <dd className="m-0 whitespace-pre-wrap">{invoice.voidReason}</dd>
                </>
              )}
              {invoice.description && (
                <>
                  <dt className="text-caption text-gray-mid uppercase tracking-label">Description</dt>
                  <dd className="m-0 whitespace-pre-wrap">{invoice.description}</dd>
                </>
              )}
            </dl>
          </section>

          {/* The contract check as it stood AT ISSUE. Both fields were
              persisted from the start and rendered nowhere — a reviewer's
              written justification for approving a difference lived only in
              the audit log, which is not where anyone reviewing the invoice
              looks. An acknowledgement nobody can see is not an audit trail. */}
          {(invoice.contractExpectedAtIssue != null
            || invoice.contractVarianceAckReason) && (
            <section data-testid="invoice-contract-check-section">
              <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
                Contract check at issue
              </h3>
              <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
                {invoice.contractExpectedAtIssue != null && (
                  <>
                    <dt className="text-caption text-gray-mid uppercase tracking-label">
                      Contract expected
                    </dt>
                    <dd className="m-0 text-right tabular-nums">
                      {fmtMoney(invoice.contractExpectedAtIssue)}
                    </dd>
                  </>
                )}
                {/* Step 6 W3 — the provenance behind that number. An expected
                    amount is not defensible on its own: "per terms effective
                    2026-07-01, band $50,000.00-$100,000.00 at 25%" is. Copied
                    at issue, so it still reads after the vendor is re-tiered
                    and the band it names no longer exists. */}
                {invoice.contractTermEffectiveFromAtIssue && (
                  <>
                    <dt className="text-caption text-gray-mid uppercase tracking-label">
                      Per terms effective
                    </dt>
                    <dd className="m-0 text-right" data-testid="invoice-contract-term-at-issue">
                      {date(invoice.contractTermEffectiveFromAtIssue)}
                    </dd>
                  </>
                )}
                {invoice.contractTierRateAtIssue != null && (
                  <>
                    <dt className="text-caption text-gray-mid uppercase tracking-label">
                      Band applied
                    </dt>
                    <dd className="m-0 text-right" data-testid="invoice-contract-tier-at-issue">
                      {describeBandAtIssue(invoice)}
                    </dd>
                  </>
                )}
                {invoice.contractVarianceAckReason && (
                  <>
                    <dt className="text-caption text-gray-mid uppercase tracking-label">
                      Difference approved
                    </dt>
                    <dd
                      className="m-0 whitespace-pre-wrap"
                      data-testid="invoice-variance-ack-reason"
                    >
                      {invoice.contractVarianceAckReason}
                    </dd>
                  </>
                )}
              </dl>
            </section>
          )}

          {detail!.stripePayment && (
            <section data-testid="invoice-stripe-section">
              <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
                Online payment
              </h3>
              <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0">
                <dt className="text-caption text-gray-mid uppercase tracking-label">Stripe intent</dt>
                <dd className="m-0 break-all">{detail!.stripePayment.paymentIntentId}</dd>
                <dt className="text-caption text-gray-mid uppercase tracking-label">Last event</dt>
                <dd className="m-0">
                  {detail!.stripePayment.lastEventAt
                    ? `${detail!.stripePayment.lastEventType ?? "event"} · ${date(detail!.stripePayment.lastEventAt)}`
                    : "No webhook event received yet"}
                </dd>
              </dl>
              <Button
                variant="secondary"
                size="sm"
                className="mt-space-2"
                onClick={() => void onRefreshPayment()}
                disabled={refreshMutation.isPending}
                data-testid="invoice-refresh-payment-button"
              >
                {refreshMutation.isPending ? "Refreshing…" : "Refresh from Stripe"}
              </Button>
            </section>
          )}

          <section>
            <h3 className="font-display font-semibold text-h4 m-0 mb-space-2">
              Payment history
            </h3>
            {detail!.payments.length === 0 ? (
              <p className="text-small text-gray-mid italic m-0">No payments recorded.</p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
                {detail!.payments.map((p) => (
                  <li
                    key={p.id}
                    className="flex flex-col gap-space-1 text-small border-b border-border-light pb-space-1"
                    data-testid={`invoice-payment-row-${p.id}`}
                  >
                    <span className="flex items-baseline justify-between gap-space-2">
                      {/* Struck through, not hidden: the receipt happened, and
                          then it was reversed. Removing the line would claim
                          the money never arrived. */}
                      <span className={p.reversedAt ? "line-through text-gray-mid" : undefined}>
                        {/* MIXED, same as `invoice.paidAt` above. */}
                        {formatCalendarDate(p.receivedAt)} · {p.method}
                        {p.note ? ` — ${p.note}` : ""}
                      </span>
                      <span
                        className={`text-right tabular-nums ${
                          p.reversedAt ? "line-through text-gray-mid" : ""
                        }`}
                      >
                        {fmtMoney(p.amount)}
                      </span>
                    </span>
                    {p.reversedAt && (
                      <span
                        className="text-caption text-status-error"
                        data-testid={`invoice-payment-reversed-${p.id}`}
                      >
                        Reversed {date(p.reversedAt)}
                        {p.reversalReason ? ` — ${p.reversalReason}` : ""}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {mode === "void" && (
            <ActionSection
              title="Void invoice"
              hint="A void is permanent and requires a reason. The invoice number is never reused."
              confirmLabel="Void invoice"
              confirmVariant="destructive"
              disabled={!reason.trim() || voidMutation.isPending}
              onConfirm={() => void onVoid()}
              onCancel={resetForms}
            >
              <ReasonInput value={reason} onChange={setReason} testId="invoice-void-reason" />
            </ActionSection>
          )}

          {mode === "payment" && (
            <ActionSection
              title="Record manual payment"
              hint={`Full payment of ${fmtMoney(invoice.amount)} — partial payments arrive with online payment.`}
              confirmLabel="Record payment"
              confirmVariant="primary"
              disabled={paymentMutation.isPending}
              onConfirm={() => void onRecordPayment()}
              onCancel={resetForms}
            >
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Received on</span>
                <input
                  type="date"
                  className="h-9 px-space-2 border border-border-light rounded-md text-small"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  data-testid="invoice-payment-date"
                />
              </label>
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">Note</span>
                <input
                  className="h-9 px-space-2 border border-border-light rounded-md text-small"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="e.g. Check #1042"
                  data-testid="invoice-payment-note"
                />
              </label>
            </ActionSection>
          )}

          {mode === "correct" && (
            <ActionSection
              title="Correct status (admin)"
              hint="Bypasses the normal lifecycle — use for refunds handled at the processor or data-entry mistakes. Reason required; the change is audited."
              confirmLabel="Correct status"
              confirmVariant="destructive"
              disabled={!reason.trim() || correctMutation.isPending}
              onConfirm={() => void onCorrect()}
              onCancel={resetForms}
            >
              <label className="flex flex-col gap-space-1 text-caption">
                <span className="text-gray-mid uppercase tracking-label">New status</span>
                <select
                  className="h-9 px-space-2 border border-border-light rounded-md text-small bg-white-pure"
                  value={correctTo}
                  onChange={(e) => setCorrectTo(e.target.value as VendorInvoiceStatus)}
                  data-testid="invoice-correct-status"
                >
                  {(Object.keys(INVOICE_STATUS_LABEL) as VendorInvoiceStatus[])
                    .filter((s) => s !== invoice.status)
                    .map((s) => (
                      <option key={s} value={s}>{INVOICE_STATUS_LABEL[s]}</option>
                    ))}
                </select>
              </label>
              <ReasonInput value={reason} onChange={setReason} testId="invoice-correct-reason" />
            </ActionSection>
          )}

          {canCorrect && mode !== "correct" && (
            <button
              type="button"
              className="self-start text-caption text-gray-mid underline hover:text-onyx"
              onClick={() => setMode("correct")}
              data-testid="invoice-correct-link"
            >
              Correct status (admin)
            </button>
          )}
        </div>
      )}
    </ResourceDrawer>
  );
}

function ActionSection({
  title,
  hint,
  confirmLabel,
  confirmVariant,
  disabled,
  onConfirm,
  onCancel,
  children,
}: {
  title: string;
  hint: string;
  confirmLabel: string;
  confirmVariant: "primary" | "destructive";
  disabled: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-md bg-white-warm p-space-3 flex flex-col gap-space-2">
      <h4 className="font-display font-semibold text-small m-0">{title}</h4>
      <p className="text-caption text-gray-mid m-0">{hint}</p>
      {children}
      <div className="flex justify-end gap-space-2">
        <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
        <Button
          variant={confirmVariant}
          size="sm"
          onClick={onConfirm}
          disabled={disabled}
          data-testid="invoice-action-confirm"
        >
          {confirmLabel}
        </Button>
      </div>
    </section>
  );
}

function ReasonInput({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  testId: string;
}) {
  return (
    <label className="flex flex-col gap-space-1 text-caption">
      <span className="text-gray-mid uppercase tracking-label">Reason (required)</span>
      <textarea
        className="min-h-[3.5rem] px-space-2 py-space-1 border border-border-light rounded-md text-small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </label>
  );
}
